import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { ConfirmationStatus } from '@reditelji/shared';
import { useState } from 'react';
import { useCancelPlan, useVolunteerMutations, type CancelFreedShift } from '../api/volunteers';
import { range } from '../timeline/derive';
import { cancelFacts, commitLabel, planRows, recordedAt, type PlanRow } from './cancelCopy';
import { bySuffix, useCoordinatorLabel } from '../lib/people';

/** Who the dialog is about — what the roster and the board both carry. */
export interface CancelTarget {
  id: string;
  name: string;
  confirmationStatus: ConfirmationStatus;
  confirmationComment?: string;
}

/**
 * "Ne pride…" — marking somebody who withdrew from the event (SPEC §1.1K).
 *
 * One dialog for every entry point, shaped like AvailabilityConfirm: what the
 * action does to each shift, the few facts worth knowing first, and the reason.
 * The button names what it frees. The plan shown is the one the server must
 * still hold when the button is pressed — a shift that started or was checked
 * in to since answers 409, and the dialog reloads rather than freeing something
 * nobody saw.
 */
export function CancelDialog({
  target,
  onClose,
  onDone,
}: {
  target: CancelTarget;
  onClose: () => void;
  /** What was freed, for the strip that offers the replacement search and undo. */
  onDone: (freed: CancelFreedShift[]) => void;
}) {
  const labelOf = useCoordinatorLabel();
  const plan = useCancelPlan(target.id);
  const mut = useVolunteerMutations();
  // A refusal usually says why they are not coming; start from their own words.
  const [reason, setReason] = useState(() =>
    target.confirmationStatus === 'zavrnjen' ? (target.confirmationComment ?? '') : '',
  );
  const p = plan.data;
  const rows = p ? planRows(p) : [];
  const n = p?.free.length ?? 0;

  return (
    <Modal opened onClose={onClose} size="md" title={`${target.name} — ne pride na dogodek`}>
      {!p ? (
        <Text size="sm" c="dimmed">
          {plan.isError ? 'Izmen ni bilo mogoče naložiti.' : 'Nalagam izmene …'}
        </Text>
      ) : p.cancelled ? (
        // Somebody else marked them while this was open: nothing left to free,
        // and no strip that would offer to undo their mark.
        <Stack gap={10}>
          <Text size="sm">
            Že zabeleženo {recordedAt(p.cancelled.at)}
            {bySuffix(labelOf(p.cancelled.byId, p.cancelled.byName, ''))}. Kaj je bilo sproščeno, je na kartici osebe.
          </Text>
          <Group justify="flex-end">
            <Button size="compact-sm" variant="default" onClick={onClose}>
              Zapri
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap={10}>
          <div>
            <Text size="sm">
              {n
                ? 'Sprostijo se vse izmene do konca dogodka, ki se še niso začele.'
                : 'Nobena izmena se ne sprosti.'}
            </Text>
            <Text size="xs" c="dimmed">
              Samo en dan? Na časovnici sprosti posamezne izmene.
            </Text>
          </div>

          {byDay(rows).map(([heading, dayRows]) => (
            <div key={heading}>
              <Text size="xs" fw={700} c="dimmed" mb={4}>
                {heading}
              </Text>
              <Stack gap={4}>
                {dayRows.map((row) => (
                  <ShiftLine key={row.shiftId} row={row} />
                ))}
              </Stack>
            </div>
          ))}
          {rows.length === 0 ? (
            <Text size="sm" c="dimmed">
              Oseba nima izmen.
            </Text>
          ) : null}

          <Stack gap={7}>
            {cancelFacts(p).map((f) => (
              <Group key={f.text} gap={7} wrap="nowrap" align="flex-start">
                <span
                  aria-hidden
                  style={{ color: f.tone, fontWeight: 700, width: '1.05rem', textAlign: 'center', flexShrink: 0 }}
                >
                  {f.icon}
                </span>
                <Text size="sm" style={{ minWidth: 0 }}>
                  {f.text}
                </Text>
              </Group>
            ))}
          </Stack>

          <Textarea
            label="Razlog (neobvezno)"
            placeholder="Npr. bolezen, poškodba, službena obveznost"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={1000}
          />

          <Group justify="flex-end" gap={6}>
            <Button
              size="compact-sm"
              style={{ background: 'oklch(0.35 0.05 245)' }}
              loading={mut.cancel.isPending}
              // A plan shown from the cache is being re-read: commit only the
              // one the server holds now.
              disabled={plan.isFetching}
              onClick={() =>
                mut.cancel.mutate(
                  {
                    id: target.id,
                    reason: reason.trim(),
                    expectFree: p.free.map((f) => f.shiftId),
                    expectRunning: p.free.filter((f) => f.running).map((f) => f.shiftId),
                  },
                  {
                    onSuccess: (res) => {
                      onClose();
                      if (res.already) {
                        notifications.show({
                          color: 'yellow',
                          message: `${target.name} — medtem je že zabeleženo, da ne pride. Kaj je bilo sproščeno, je na kartici.`,
                        });
                        return;
                      }
                      onDone(res.freed);
                    },
                    onError: () => void plan.refetch(),
                  },
                )
              }
            >
              {commitLabel(n)}
            </Button>
            <Button size="compact-sm" variant="default" onClick={onClose}>
              Prekliči
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function byDay(rows: PlanRow[]): [string, PlanRow[]][] {
  const out: [string, PlanRow[]][] = [];
  for (const r of rows) {
    const last = out.at(-1);
    if (last && last[0] === r.heading) last[1].push(r);
    else out.push([r.heading, [r]]);
  }
  return out;
}

function ShiftLine({ row }: { row: PlanRow }) {
  const free = row.outcome === 'free';
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap={8}
      style={{
        padding: '6px 9px',
        border: '1px solid oklch(0.94 0.005 95)',
        borderRadius: 8,
        background: free ? 'oklch(1 0 0)' : 'oklch(0.99 0.002 95)',
      }}
    >
      <div style={{ minWidth: 0, opacity: free ? 1 : 0.7 }}>
        <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <b style={{ fontFamily: 'var(--rr-font-mono)', marginRight: 5 }}>{row.postCode}</b>
          {row.postMicro}
          {row.urgent ? (
            <span
              style={{ marginLeft: 5, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', color: 'oklch(0.55 0.12 25)' }}
            >
              NUJNO
            </span>
          ) : null}
        </Text>
        <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--rr-font-mono)' }}>
          {range(row.fromMin, row.toMin)}
        </Text>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <span
          style={
            free
              ? {
                  display: 'inline-block',
                  fontFamily: 'var(--rr-font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 5,
                  border: '1px dashed oklch(0.86 0.008 95)',
                  background: 'oklch(0.965 0.004 95)',
                  color: 'oklch(0.42 0.012 90)',
                }
              : {
                  display: 'inline-block',
                  fontSize: 11.5,
                  padding: '1px 7px',
                  borderRadius: 5,
                  background: 'oklch(0.965 0.005 95)',
                  color: 'oklch(0.5 0.012 90)',
                }
          }
        >
          {free ? '→ PROSTO' : 'ostane'}
        </span>
        {row.note ? (
          <Text
            size="xs"
            style={{
              color: row.hot ? 'oklch(0.5 0.13 25)' : 'var(--rr-text-muted)',
              fontWeight: row.hot ? 700 : 400,
            }}
          >
            {row.note}
          </Text>
        ) : null}
      </div>
    </Group>
  );
}
